import React, { FC, PropsWithChildren, ReactElement } from 'react';
import { Button } from '@/elements/Button';
import { TabsTheme } from './TabsTheme';
import { useComponentTheme, cn } from '@/utils';
import { motion } from 'motion/react';

export interface TabProps extends PropsWithChildren {
  /**
   * The id of the tab list.
   * @private
   */
  id?: string;

  /**
   * The class name to be added to the tab container.
   */
  containerClassName?: string;

  /**
   * The class name to be added to the tab.
   */
  className?: string;

  /**
   * The disabled state of the tab.
   *
   * @private
   */
  disabled?: boolean;

  /**
   * The selected state of the tab.
   *
   * @private
   */
  selected?: boolean;

  /**
   * The callback to be called when the tab is selected.
   *
   * @private
   */
  onSelect?: () => void;

  /**
   * Element to display before the Button content.
   */
  start?: ReactElement;

  /**
   * Element to display after the Button content.
   */
  end?: ReactElement;

  /**
   * The size of the tabs.
   *
   * @private
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large' | string;

  /**
   * Theme for the Tabs.
   */
  theme?: TabsTheme;
}

export const Tab: FC<TabProps> = ({
  children,
  id,
  selected,
  containerClassName,
  className,
  disabled,
  start,
  end,
  onSelect,
  size = 'medium',
  theme: customTheme
}) => {
  const theme: TabsTheme = useComponentTheme('tabs', customTheme);

  return (
    <span className={cn(theme.list.tab.base, containerClassName)}>
      <Button
        start={start}
        end={end}
        className={cn(
          theme.list.tab.button,
          className,
          {
            [theme.list.tab.disabled]: disabled,
            [theme.list.tab.selected]: selected
          },
          theme.list.tab.size?.[size]
        )}
        disabled={disabled}
        role="tab"
        variant="text"
        aria-selected={selected ? 'true' : 'false'}
        aria-disabled={disabled ? 'true' : 'false'}
        onClick={() => {
          if (!disabled && onSelect) {
            onSelect();
          }
        }}
      >
        {children}
      </Button>
      {selected && (
        <motion.div
          className={cn(
            theme.list.indicator?.base,
            theme.list.indicator?.size?.[size]
          )}
          layoutId={`${id}-tabs-underline`}
        />
      )}
    </span>
  );
};
